import { Dispatch, SetStateAction, useEffect } from "react";
import Text from "@tiptap/extension-text";
import Bold from "@tiptap/extension-bold";
import Strike from "@tiptap/extension-strike";
import Italic from "@tiptap/extension-italic";
import Heading from "@tiptap/extension-heading";
import { Color } from "@tiptap/extension-color";
import Document from "@tiptap/extension-document";
import ListItem from "@tiptap/extension-list-item";
import Highlight from "@tiptap/extension-highlight";
import Paragraph from "@tiptap/extension-paragraph";
import Underline from "@tiptap/extension-underline";
import TextStyle from "@tiptap/extension-text-style";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import { Editor, EditorContent, useEditor } from "@tiptap/react";

import {
	Bold as B,
	Italic as I,
	Underline as U,
	Strikethrough as S,
	List as BL,
	ListOrdered as OL,
	Heading as H,
	Heading1 as H1,
	Heading2 as H2,
	Heading3 as H3,
	Heading4 as H4,
	Type as T,
	Baseline as LC,
	Palette as P,
} from "lucide-react";

import { cn } from "../../Utils/Utils";
import { Separator } from "../ui/separator";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import "./Richtext.css";

interface Props {
	value: string;
	setValue: Dispatch<SetStateAction<string>>; //(value: String) => void;
	className?: string;
}

const TiptapRichTextEditor = ({ value, setValue, className }: Props) => {
	const editor = useEditor({
		immediatelyRender: false,
		content: value ?? "<p></p>",
		onUpdate: ({ editor }) => setValue(editor.getHTML()),
		extensions: [
			Document,
			Paragraph,
			Text,
			Bold,
			Italic,
			Underline,
			Strike,
			Color,
			TextStyle,
			ListItem,
			BulletList,
			OrderedList,
			Highlight.configure({ multicolor: true }),
			Heading.configure({
				levels: [1, 2, 3, 4],
			}),
		],
	});

	useEffect(() => {
		if (editor && value !== editor.getHTML()) {
			editor.commands.setContent(value);
		}
	}, [value, editor]);

	if (!editor) {
		return null;
	}

	return (
		<div className='w-full p-2 border-2 rounded-xl h-fit'>
			<div className='w-full flex flex-row justify-between items-center h-6 mb-2 px-2'>
				<button onClick={() => editor.commands.setParagraph()}>
					<T
						className={cn(
							"richtextIcons",
							editor.isActive("paragraph") ? "richtextIconsBold" : ""
						)}
					/>
				</button>
				<Separator orientation='vertical' />
				<HeadingComponent editor={editor} />
				<Separator orientation='vertical' />
				<button onClick={() => editor.chain().focus().toggleBold().run()}>
					<B
						className={cn(
							"richtextIcons",
							editor.isActive("bold") ? "richtextIconsBold" : ""
						)}
					/>
				</button>
				<Separator orientation='vertical' />
				<button onClick={() => editor.chain().focus().toggleItalic().run()}>
					<I
						className={cn(
							"richtextIcons",
							editor.isActive("italic") ? "richtextIconsBold" : ""
						)}
					/>
				</button>
				<Separator orientation='vertical' />
				<button onClick={() => editor.chain().focus().toggleUnderline().run()}>
					<U
						className={cn(
							"richtextIcons",
							editor.isActive("underline") ? "richtextIconsBold" : ""
						)}
					/>
				</button>
				<Separator orientation='vertical' />
				<button onClick={() => editor.chain().focus().toggleStrike().run()}>
					<S
						className={cn(
							"richtextIcons",
							editor.isActive("strike") ? "richtextIconsBold" : ""
						)}
					/>
				</button>
				<Separator orientation='vertical' />
				<button onClick={() => editor.chain().focus().toggleBulletList().run()}>
					<BL
						className={cn(
							"richtextIcons",
							editor.isActive("bulletList") ? "richtextIconsBold" : ""
						)}
					/>
				</button>
				<Separator orientation='vertical' />
				<button
					onClick={() => editor.chain().focus().toggleOrderedList().run()}
				>
					<OL
						className={cn(
							"richtextIcons",
							editor.isActive("orderedList") ? "richtextIconsBold" : ""
						)}
					/>
				</button>
				<Separator orientation='vertical' />
				<TextColor editor={editor} />
				<Separator orientation='vertical' />
				<ColorPallet editor={editor} />
			</div>
			<EditorContent
				editor={editor}
				className={cn(
					"outline-none max-h-60 h-40 overflow-hidden overflow-y-auto p-2 border-[1px] border-input rounded-md",
					className
				)}
			/>
		</div>
	);
};

export default TiptapRichTextEditor;

function HeadingComponent({ editor }: { editor: Editor }) {
	return (
		<Popover>
			<PopoverTrigger className='w-fit h-fit p-0 m-0'>
				<div>
					<H
						className={cn(
							"richtextIcons",
							editor.isActive("heading") ? "richtextIconsBold" : ""
						)}
					/>
				</div>
			</PopoverTrigger>
			<PopoverContent
				className='w-fit flex flex-row justify-center items-center gap-x-4 py-3 h-10'
				side='top'
			>
				<button
					onClick={() =>
						editor.chain().focus().toggleHeading({ level: 1 }).run()
					}
				>
					<H1
						className={cn(
							"richtextIcons",
							editor.isActive("heading", { level: 1 })
								? "richtextIconsBold"
								: ""
						)}
					/>
				</button>
				<Separator orientation='vertical' />
				<button
					onClick={() =>
						editor.chain().focus().toggleHeading({ level: 2 }).run()
					}
				>
					<H2
						className={cn(
							"richtextIcons",
							editor.isActive("heading", { level: 2 })
								? "richtextIconsBold"
								: ""
						)}
					/>
				</button>
				<Separator orientation='vertical' />
				<button
					onClick={() =>
						editor.chain().focus().toggleHeading({ level: 3 }).run()
					}
				>
					<H3
						className={cn(
							"richtextIcons",
							editor.isActive("heading", { level: 3 })
								? "richtextIconsBold"
								: ""
						)}
					/>
				</button>
				<Separator orientation='vertical' />
				<button
					onClick={() =>
						editor.chain().focus().toggleHeading({ level: 4 }).run()
					}
				>
					<H4
						className={cn(
							"richtextIcons",
							editor.isActive("heading", { level: 4 })
								? "richtextIconsBold"
								: ""
						)}
					/>
				</button>
			</PopoverContent>
		</Popover>
	);
}

function ColorPallet({ editor }: { editor: Editor }) {
	return (
		<Popover>
			<PopoverTrigger className='w-fit h-fit p-0 m-0'>
				<div className={editor.isActive("bold") ? "richtextIconsBold" : ""}>
					<P
						className={cn(
							"richtextIcons",
							editor.isActive("highlight", { color: "#ffc078" })
								? "text-[#ffc078]"
								: editor.isActive("highlight", { color: "#ffffff" })
								? "text-slate-400"
								: editor.isActive("highlight", { color: "#e60000" })
								? "text-[#e60000]"
								: editor.isActive("highlight", { color: "#ff9900" })
								? "text-[#ff9900]"
								: ""
						)}
					/>
				</div>
			</PopoverTrigger>
			<PopoverContent
				className='w-fit flex flex-row justify-center items-center gap-x-2 py-3 h-10'
				side='top'
			>
				<button
					onClick={() =>
						editor.chain().focus().toggleHighlight({ color: "#000000" }).run()
					}
					className={cn(
						"bg-[#000000] size-4",
						editor.isActive("bold") ? "richtextIconsBold" : ""
					)}
				/>
				<Separator orientation='vertical' />
				<button
					onClick={() =>
						editor.chain().focus().toggleHighlight({ color: "#ffffff" }).run()
					}
					className={cn(
						"bg-[#ffffff] size-4 border-[1px] border-input",
						editor.isActive("bold") ? "richtextIconsBold" : ""
					)}
				/>
				<Separator orientation='vertical' />
				<button
					onClick={() =>
						editor.chain().focus().toggleHighlight({ color: "#e60000" }).run()
					}
					className={cn(
						"bg-[#e60000] size-4",
						editor.isActive("bold") ? "richtextIconsBold" : ""
					)}
				/>
				<Separator orientation='vertical' />
				<button
					onClick={() =>
						editor.chain().focus().toggleHighlight({ color: "#ff9900" }).run()
					}
					className={cn(
						"bg-[#ff9900] size-4",
						editor.isActive("bold") ? "richtextIconsBold" : ""
					)}
				/>
			</PopoverContent>
		</Popover>
	);
}
function TextColor({ editor }: { editor: Editor }) {
	return (
		<Popover>
			<PopoverTrigger className='w-fit h-fit p-0 m-0'>
				<div>
					<LC
						className={cn(
							"richtextIcons",
							editor.getAttributes("textStyle").color === "#000000"
								? `text-[#000000]`
								: editor.getAttributes("textStyle").color === "#ffffff"
								? `text-slate-400`
								: editor.getAttributes("textStyle").color === "#facccc"
								? `text-[#fdaeae]`
								: "text-black"
						)}
					/>
				</div>
			</PopoverTrigger>
			<PopoverContent
				className='w-fit flex flex-row justify-center items-center gap-x-2 py-3 h-10'
				side='top'
			>
				<button
					onClick={() => editor.chain().focus().setColor("#000000").run()}
					className={cn("bg-[#000000] size-4")}
					data-testid='setBlack'
				/>
				<Separator orientation='vertical' />
				<button
					onClick={() => editor.chain().focus().setColor("#ffffff").run()}
					className={cn("bg-[#ffffff] size-4 border-[1px] border-input")}
					data-testid='setWhite'
				/>
				<Separator orientation='vertical' />
				<button
					onClick={() => editor.chain().focus().setColor("#facccc").run()}
					className={cn("bg-[#facccc] size-4")}
					data-testid='setRed'
				/>
			</PopoverContent>
		</Popover>
	);
}
